/******************************************************************************
 *
 * FILE:
 * src/modules/wms/workspace/approval/pages/ApprovalWorkspacePage.jsx
 *
 * MODULE:
 * Work Management System (WMS)
 *
 * FEATURE:
 * Approval Workspace
 *
 * DESCRIPTION:
 * Main Approval Workspace page.
 *
 * RESPONSIBILITIES
 * ----------------
 * ✓ Load approval workspace
 * ✓ Maintain page state
 * ✓ Delegate actions to child components
 * ✓ No business logic
 *
 ******************************************************************************/

import React, {
    useCallback,
    useEffect,
    useState,
} from "react";

import { useParams } from "react-router-dom";

import approvalWorkspaceService
    from "../services/approvalWorkspaceService";

import ExecutionHeader
    from "../../execution/components/ExecutionHeader";

import ExecutionInformationPanel
    from "../../execution/components/ExecutionInformationPanel";

import AttachmentPanel
    from "../../execution/components/AttachmentPanel";

import CommentPanel
    from "../../execution/components/CommentPanel";

import ActivityTimeline
    from "../../execution/components/ActivityTimeline";

import CommentsModal from "../../components/CommentsModal";

// Uncomment this import if this component already exists
// import ExecutionChecklistPanel
//     from "../../execution/components/ExecutionChecklistPanel";

import ApprovalFooter
    from "../components/ApprovalFooter";

export default function ApprovalWorkspacePage() {

    // ==========================================================
    // ROUTE
    // ==========================================================

    const { workId } = useParams();

    // ==========================================================
    // STATE
    // ==========================================================

    const [loading, setLoading] =
        useState(true);

    const [workspace, setWorkspace] =
        useState(null);

    const [showComments, setShowComments] = useState(false);

    // ==========================================================
    // LOAD WORKSPACE
    // ==========================================================

    const loadWorkspace =
        useCallback(async () => {

            try {

                setLoading(true);

                const response =
                    await approvalWorkspaceService
                        .getApprovalWorkspaceDetails(workId);

                setWorkspace(response);

            }

            catch (error) {

                console.error(
                    "Failed to load Approval Workspace",
                    error
                );

                setWorkspace(null);

            }

            finally {

                setLoading(false);

            }

        }, [workId]);

    
    const handleAddComment =
        useCallback(async (commentText) => {

            try {

                await approvalWorkspaceService.addComment(

                    workId,
                    commentText

                );

                await loadWorkspace();

            }

            catch (error) {

                console.error(

                    "Failed to add comment",

                    error

                );

            }

        }, [

            workId,
            loadWorkspace

        ]);


    useEffect(() => {

        loadWorkspace();

    }, [loadWorkspace]);

    // ==========================================================
    // LOADING
    // ==========================================================

    if (loading) {

        return (

            <div className="container-fluid py-5">

                <div className="text-center">

                    <div className="spinner-border text-primary" />

                    <div className="mt-3">

                        Loading Approval Workspace...

                    </div>

                </div>

            </div>

        );

    }

    // ==========================================================
    // ERROR
    // ==========================================================

    if (!workspace) {

        return (

            <div className="container-fluid py-5">

                <div className="alert alert-danger">

                    Unable to load Approval Workspace.

                </div>

            </div>

        );

    }

    // ==========================================================
    // RENDER
    // ==========================================================

    return (

    <>
        <div
            id="approval-loading-overlay"
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "rgba(255,255,255,0.75)",
                display: "none",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
            }}
        >
            <div className="text-center">
                <div
                    className="spinner-border text-primary"
                    style={{ width: "3rem", height: "3rem" }}
                />
                <div className="mt-3 fw-semibold">
                    Processing...
                </div>
            </div>
        </div>

        <div className="container-fluid pb-5">

            <ExecutionHeader
                work={workspace.work}
            />

            <ExecutionInformationPanel
                work={workspace.work}
                summary={workspace.summary}
            />

            {/*
            Uncomment after verifying the component exists.

            <ExecutionChecklistPanel
                checklist={workspace.checklist}
            />
            */}

            <AttachmentPanel
                attachments={workspace.attachments}
            />

            <CommentPanel
                comments={workspace.comments}
                onAddComment={handleAddComment}
                onShowAll={() => setShowComments(true)}
            />

            <CommentsModal
                show={showComments}
                onClose={() => setShowComments(false)}
                comments={workspace.comments}
                onAddComment={handleAddComment}
                title={`Comments - ${workspace.work?.work_number || ""}`}
            />

            <ActivityTimeline
                timeline={workspace.timeline}
            />

            <ApprovalFooter
                work={workspace.work}
                permissions={workspace.permissions}
                onRefresh={loadWorkspace}
                setPageLoading={setLoading}
            />

        </div>
        </>        
    );

}